import { useMemo, useState } from 'react'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { api, ApiError, type Memory, type MemoryStatus } from '../../api/client'
import { EXTERNAL_ORIGINS, type MemoryOrigin } from '@agent/shared'

// ISSUE #23: Memory Manager UI。
//
// - status タブ (candidate / trusted / quarantined) で切替
// - テーブル: summary / kind / origin / final_score (= 表示なし; 仕様の trust_score などを代用)
// - 行クリック → 詳細パネル (origin_signature, attestation_chain_json, full summary)
// - candidate のみ Approve / Reject ボタン
// - external-* origin の approve 時は警告モーダル
//
// archived (deprecated_fact かつ status='archived') は Phase 4+ スコープのため未実装。

type TabStatus = Extract<MemoryStatus, 'candidate' | 'trusted' | 'quarantined'>
const TABS: ReadonlyArray<{ key: TabStatus; label: string }> = [
  { key: 'candidate', label: 'Candidate' },
  { key: 'trusted', label: 'Trusted' },
  { key: 'quarantined', label: 'Quarantined' },
]

const EXTERNAL_ORIGIN_SET = new Set<MemoryOrigin>(EXTERNAL_ORIGINS as readonly MemoryOrigin[])

function isExternalOrigin(origin: MemoryOrigin): boolean {
  return EXTERNAL_ORIGIN_SET.has(origin)
}

export function MemoryListPage() {
  const [tab, setTab] = useState<TabStatus>('candidate')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [pendingApproval, setPendingApproval] = useState<Memory | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['memories', { status: tab }],
    queryFn: () => api.listMemories({ status: tab, limit: 50 }),
    placeholderData: keepPreviousData,
  })

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.approveMemory(id),
    onSuccess: async () => {
      setPendingApproval(null)
      setActionError(null)
      await queryClient.invalidateQueries({ queryKey: ['memories'] })
    },
    onError: (err) => setActionError(formatError(err)),
  })

  const rejectMutation = useMutation({
    mutationFn: (id: string) => api.rejectMemory(id),
    onSuccess: async () => {
      setActionError(null)
      await queryClient.invalidateQueries({ queryKey: ['memories'] })
    },
    onError: (err) => setActionError(formatError(err)),
  })

  const memories = query.data?.memories ?? []
  const selected = useMemo(
    () => memories.find((m) => m.id === selectedId) ?? null,
    [memories, selectedId],
  )

  const onApproveClick = (memory: Memory) => {
    setActionError(null)
    if (isExternalOrigin(memory.origin)) {
      // 外部由来 → 警告モーダルを挟む
      setPendingApproval(memory)
    } else {
      approveMutation.mutate(memory.id)
    }
  }

  const onRejectClick = (memory: Memory) => {
    setActionError(null)
    rejectMutation.mutate(memory.id)
  }

  const confirmExternalApprove = () => {
    if (pendingApproval) {
      approveMutation.mutate(pendingApproval.id)
    }
  }

  return (
    <section className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Memories</h1>
        <p className="text-sm text-slate-500">
          Review candidate memories extracted from job executions. Approve to promote them to
          trusted (used in future context packets), or reject to quarantine.
        </p>
      </div>

      <div className="flex items-center gap-2 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => {
              setTab(t.key)
              setSelectedId(null)
              setActionError(null)
            }}
            className={
              tab === t.key
                ? 'border-b-2 border-slate-900 px-3 py-2 text-sm font-medium text-slate-900'
                : 'border-b-2 border-transparent px-3 py-2 text-sm text-slate-500 hover:text-slate-900'
            }
          >
            {t.label}
          </button>
        ))}
        <div className="ml-auto text-xs text-slate-400">
          {query.isFetching ? 'Refreshing…' : ' '}
        </div>
      </div>

      {actionError && (
        <div className="rounded border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {actionError}
        </div>
      )}

      {query.isPending ? (
        <p className="text-slate-500">Loading…</p>
      ) : query.isError ? (
        <ErrorBox error={query.error} />
      ) : memories.length === 0 ? (
        <p className="text-slate-500">No memories in this state.</p>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]">
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2">Summary</th>
                  <th className="px-4 py-2">Kind</th>
                  <th className="px-4 py-2">Origin</th>
                  <th className="px-4 py-2">Score</th>
                  <th className="px-4 py-2">Created</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {memories.map((m) => (
                  <tr
                    key={m.id}
                    onClick={() => setSelectedId(m.id)}
                    className={
                      m.id === selectedId
                        ? 'cursor-pointer bg-slate-100'
                        : 'cursor-pointer hover:bg-slate-50'
                    }
                  >
                    <td className="max-w-md truncate px-4 py-2" title={m.summary}>
                      {m.summary}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-600">{m.kind}</td>
                    <td className="px-4 py-2">
                      <OriginBadge origin={m.origin} />
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-600">
                      {scoreSummary(m)}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500">
                      {new Date(m.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {tab === 'candidate' ? (
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            disabled={
                              approveMutation.isPending || rejectMutation.isPending
                            }
                            onClick={(e) => {
                              e.stopPropagation()
                              onApproveClick(m)
                            }}
                            className="rounded bg-emerald-600 px-2 py-1 text-xs text-white hover:bg-emerald-700 disabled:opacity-50"
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            disabled={
                              approveMutation.isPending || rejectMutation.isPending
                            }
                            onClick={(e) => {
                              e.stopPropagation()
                              onRejectClick(m)
                            }}
                            className="rounded border border-slate-300 bg-white px-2 py-1 text-xs hover:bg-slate-100 disabled:opacity-50"
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <MemoryDetailPanel memory={selected} />
        </div>
      )}

      {pendingApproval && (
        <ExternalApprovalModal
          memory={pendingApproval}
          isPending={approveMutation.isPending}
          onCancel={() => setPendingApproval(null)}
          onConfirm={confirmExternalApprove}
        />
      )}
    </section>
  )
}

function scoreSummary(m: Memory): string {
  // テーブルの "Score" カラム。Memory には final_score が無いので
  // trust × utility × safety を簡易表示。
  const composite = m.trust_score * m.utility_score * m.safety_score
  return composite.toFixed(2)
}

function OriginBadge({ origin }: { origin: MemoryOrigin }) {
  const external = isExternalOrigin(origin)
  const className = external
    ? 'rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800'
    : 'rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-700'
  return <span className={className}>{origin}</span>
}

function MemoryDetailPanel({ memory }: { memory: Memory | null }) {
  if (!memory) {
    return (
      <aside className="rounded-lg border border-dashed border-slate-200 bg-white p-6 text-sm text-slate-500">
        Select a row to inspect provenance.
      </aside>
    )
  }
  return (
    <aside className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Memory detail
        </h2>
        <p className="mt-1 font-mono text-xs text-slate-500">{memory.id}</p>
      </div>

      <Field label="Summary">
        <p className="whitespace-pre-wrap text-sm text-slate-800">{memory.summary}</p>
      </Field>

      <Field label="Origin / source">
        <p className="text-sm text-slate-800">
          <OriginBadge origin={memory.origin} />{' '}
          <span className="ml-2 font-mono text-xs text-slate-600">{memory.source}</span>
        </p>
        {memory.source_uri && (
          <a
            href={memory.source_uri}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-block break-all text-xs text-blue-700 underline"
          >
            {memory.source_uri}
          </a>
        )}
      </Field>

      <Field label="Signature">
        <p className="break-all font-mono text-xs text-slate-600">
          {memory.origin_signature ?? '—'}
        </p>
      </Field>

      <Field label="Attestation chain">
        {memory.attestation_chain && memory.attestation_chain.length > 0 ? (
          <pre className="overflow-x-auto rounded bg-slate-900 p-2 text-xs text-slate-100">
            {JSON.stringify(memory.attestation_chain, null, 2)}
          </pre>
        ) : (
          <p className="text-xs text-slate-500">No chain recorded.</p>
        )}
      </Field>

      <Field label="Scores">
        <dl className="grid grid-cols-2 gap-2 text-xs text-slate-700">
          <ScoreEntry label="trust" value={memory.trust_score} />
          <ScoreEntry label="utility" value={memory.utility_score} />
          <ScoreEntry label="safety" value={memory.safety_score} />
          <ScoreEntry label="source_reliability" value={memory.source_reliability} />
        </dl>
      </Field>
    </aside>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  )
}

function ScoreEntry({ label, value }: { label: string; value: number }) {
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right font-mono">{value.toFixed(2)}</dd>
    </>
  )
}

function ExternalApprovalModal({
  memory,
  isPending,
  onCancel,
  onConfirm,
}: {
  memory: Memory
  isPending: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-slate-900">
          External-origin memory — confirm approval
        </h2>
        <p className="mt-3 text-sm text-slate-700">
          外部由来です。trusted に昇格すると将来の job context に注入されます。
        </p>
        <p className="mt-2 text-xs text-slate-500">
          Origin: <span className="font-mono">{memory.origin}</span>
          <br />
          Source: <span className="font-mono">{memory.source}</span>
        </p>
        <pre className="mt-3 max-h-40 overflow-y-auto rounded bg-slate-50 p-2 text-xs text-slate-700">
          {memory.summary}
        </pre>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="rounded bg-amber-600 px-3 py-1.5 text-sm text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {isPending ? 'Approving…' : 'Approve as trusted'}
          </button>
        </div>
      </div>
    </div>
  )
}

function ErrorBox({ error }: { error: unknown }) {
  return (
    <div className="rounded border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
      Failed to load memories — {formatError(error)}
    </div>
  )
}

function formatError(error: unknown): string {
  if (error instanceof ApiError) return `${error.code}: ${error.message}`
  if (error instanceof Error) return error.message
  return 'Unknown error'
}
